/**
 * Handle arithmetic in a float-friendly manner.
 * Unexpected results may occur if performing operations on two SensibleNumbers with differing digits.
 */
export class SensibleNumber {
	private readonly literalValue: number

	private constructor(readonly digits: number, literalValue: number) {
		// If we want to handle this better we should switch to a proper implementation (lib).
		if (digits > 4) throw new Error('Unsafe precision level')

		this.literalValue = this.sanitize(literalValue)
	}

	add(other: number | SensibleNumber): SensibleNumber {
		const otherValue = typeof other === 'number' ? other : other.literalValue

		return new SensibleNumber(this.digits, this.literalValue + otherValue)
	}

	subtract(other: number | SensibleNumber): SensibleNumber {
		const otherValue = typeof other === 'number' ? other : other.literalValue

		return new SensibleNumber(this.digits, this.literalValue - otherValue)
	}

	divideBy(other: number | SensibleNumber): SensibleNumber {
		const otherValue = typeof other === 'number' ? this.sanitize(other) : other.literalValue

		return new SensibleNumber(this.digits, this.literalValue / otherValue)
	}

	multiplyBy(other: number | SensibleNumber): SensibleNumber {
		const otherValue = typeof other === 'number' ? this.sanitize(other) : other.literalValue

		return new SensibleNumber(this.digits, this.literalValue * otherValue)
	}

	greaterThan(other: number | SensibleNumber) {
		const otherValue = typeof other === 'number' ? this.sanitize(other) : other.literalValue

		return this.literalValue > otherValue
	}

	valueOf() {
		return this.literalValue
	}

	equals(other: SensibleNumber): boolean {
		return this.literalValue === other.literalValue
	}

	private sanitize(num: number): number {
		// Round to the specified digits and re-parse as a number.
		return parseFloat(num.toFixed(this.digits))
	}

	static from(strOrNum: string | number, digits: number = 2): SensibleNumber {
		return new SensibleNumber(digits, parseFloat(strOrNum as string) || 0)
	}

	static min(a: SensibleNumber, b: SensibleNumber): SensibleNumber {
		if (a.literalValue <= b.literalValue) {
			return a
		}
		return b
	}

	static max(a: SensibleNumber, b: SensibleNumber): SensibleNumber {
		if (a.literalValue >= b.literalValue) {
			return a
		}
		return b
	}
}
